import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getAgreementPreview } from './agreement.fetchers';
import type { BffBlob } from './bff-client';

/**
 * TanStack hook for the E-SF18 unsigned contract preview. The sign page calls
 * THIS, never the fetcher.
 *
 * ── WHY THE QUERY HOLDS A `Blob` AND NOT AN OBJECT URL ─────────────────────
 * An object URL is a resource with a lifetime: create it and you must revoke it,
 * or every re-render leaks another copy of a multi-page PDF for as long as the tab
 * lives. A TanStack cache entry has no destructor, so a URL minted inside `queryFn`
 * would be a handle nobody owns. The query therefore caches the immutable BYTES;
 * `useObjectUrl` owns the URL and revokes it on unmount. One thing per owner.
 */

export const agreementQueryKeys = {
  /**
   * Keyed on (candidate, plan) — the two halves of the enrollment the BFF resolves
   * the DRAFT quote from. A shopper who goes back and changes either gets a
   * DIFFERENT key and therefore a different document, rather than the cached
   * contract for the enrollment they abandoned. (The BFF enforces this too, via
   * the enrollment fingerprint; this makes the client agree rather than rely on a
   * refusal.)
   */
  preview: (candidateId: string, planId: string) =>
    ['shop', 'agreement-preview', candidateId, planId] as const,
} as const;

export interface AgreementPreviewQueryArgs {
  readonly candidateId: string | undefined;
  readonly planId: string | undefined;
}

/**
 * Fetch the UNSIGNED contract PDF for review.
 *
 * ── RETRY IS THE CUSTOMER'S, NOT THE CLIENT'S ──────────────────────────────
 * `retry: false`, deliberately overriding the app-wide policy (which would allow
 * one automatic retry on a 5xx). E40 bounds its own render at 10 seconds and turns
 * an overrun into a typed, retryable 503 — so a silent client retry would double a
 * wait the customer is already sitting through, with no indication that anything
 * is happening. The sign step surfaces the failure and an explicit "try again"
 * instead, which is both honest and cheaper. See `retryPolicy.ts` for the QA
 * finding that made client-side retry a considered decision here.
 *
 * ── STALENESS ──────────────────────────────────────────────────────────────
 * The document is derived from a DRAFT quote that this page cannot change, and
 * E40 caches it per (quote, pricing snapshot) upstream. Refetching it on a
 * remount would re-pay the render for a document that has not moved, so the
 * fetched bytes stay fresh for the life of the step. Changing plan or meter
 * changes the KEY, which is what invalidation means here.
 */
export function useAgreementPreviewQuery(
  args: AgreementPreviewQueryArgs,
): UseQueryResult<BffBlob> {
  const { candidateId, planId } = args;
  const enabled =
    candidateId !== undefined && candidateId !== '' && planId !== undefined && planId !== '';

  return useQuery({
    queryKey: enabled
      ? agreementQueryKeys.preview(candidateId, planId)
      : (['shop', 'agreement-preview', 'disabled'] as const),
    queryFn: ({ signal }) => {
      if (!enabled) {
        throw new Error('agreement preview query fired without a candidate/plan');
      }
      return getAgreementPreview({ candidateId, planId, signal });
    },
    enabled,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
  });
}
